import LoginApi from '@api/LoginApi'
import * as actionTypes from './actionTypes'
import * as mutationTypes from './mutationTypes'
import { MAINTENANCE_MODE } from '@api/Statuses'

const setAuthError = (context, message) => {
  context.commit(mutationTypes.SET_AUTH_ERROR, true)
  context.commit(mutationTypes.SET_AUTH_MESSAGE, message)
}

const handleTokenError = (context, requestBody = {}, response = {}) => {
  context.commit(mutationTypes.SET_TOKEN_ERROR, true)
  context.commit(mutationTypes.SET_AUTH_MESSAGE, response.userMessage || response.message)
  context.commit(mutationTypes.SET_VERIFYING_TOKEN, false)

  if (requestBody.type === 'Registration') { context.dispatch(actionTypes.REQUEST_NEW_REGISTRATION_EMAIL, requestBody.userId) } else { context.dispatch(actionTypes.REQUEST_RESET_PASSWORD_INSTRUCTIONS, requestBody.userId) }
}

export default {
  [ actionTypes.VERIFY_TOKEN ] (context, options = {}) {
    context.commit(mutationTypes.INITIALIZE_TOKEN_VERIFICATION)
    return new LoginApi()
      .verifyToken({
        body: options.body,
        params: options.params
      }).then(response => {
        if (response.status === '440' || response.status === 440 || response.status === 'TokenAttemptFailed') { handleTokenError(context, options.body, response) } else { context.commit(mutationTypes.SET_VERIFYING_TOKEN, false) }
      })
      .catch(error => {
        if (error.response) {
          error.response.json().then(response => {
            handleTokenError(context, options.body, response)
          })
        } else handleTokenError(context, options.body) // because it can fail with 500, fail with 200 but status: 'SomeStatus', or fail for internal reasons.
      })
  },

  [ actionTypes.REQUEST_NEW_REGISTRATION_EMAIL ] (context, userId) {
    context.commit(mutationTypes.SET_ISSUING_NEW_REGISTRATION_EMAIL, true)
    return new LoginApi()
      .resendSignupEmail({
        body: {
          userId
        }
      })
      .then(resp => {
        context.commit(mutationTypes.SET_ISSUING_NEW_REGISTRATION_EMAIL, false)
        context.commit(mutationTypes.SET_RESET_PASSWORD_MESSAGE, resp.userMessage)
        return true
      })
      .catch(err => {
        context.commit(mutationTypes.SET_ISSUING_NEW_REGISTRATION_EMAIL, false)
        err.response.json().then(resp => {
          context.commit(mutationTypes.SET_RESET_PASSWORD_MESSAGE, resp.userMessage)
        })
        return false
      })
  },

  [ actionTypes.REQUEST_RESET_PASSWORD_INSTRUCTIONS ] (context, userId) {
    context.commit(mutationTypes.SET_ISSUING_RESET_PASSWORD_INSTRUCTIONS, true)
    return new LoginApi()
      .requestResetPasswordInstructions({
        body: {
          userId
        }
      })
      .then(resp => {
        context.commit(mutationTypes.SET_ISSUING_RESET_PASSWORD_INSTRUCTIONS, false)
        context.commit(mutationTypes.SET_RESET_PASSWORD_MESSAGE, resp.userMessage)
        return true
      })
      .catch(err => {
        context.commit(mutationTypes.SET_ISSUING_RESET_PASSWORD_INSTRUCTIONS, false)
        err.response.json().then(resp => {
          context.commit(mutationTypes.SET_RESET_PASSWORD_MESSAGE, resp.userMessage)
        })
        return false
      })
  },

  [ actionTypes.GET_NEW_TOKEN ] (context, options = {}) {
    context.commit(mutationTypes.SET_REQUESTING_NEW_TOKEN, true)
    return new LoginApi({
      simulateExample: 'Sucess'
    })
      .requestToken({
        body: options.body
      })
      .then(response => {
        context.commit(mutationTypes.SET_REQUESTING_NEW_TOKEN, false)
        return true
      })
      .catch(_ => {
        context.commit(mutationTypes.SET_REQUESTING_NEW_TOKEN, false)
        return false
      })
  },

  // If you forgot your password, you'd want a link, you'd click that
  // then you'd be able to reset your password.
  [ actionTypes.RESET_PASSWORD ] (context, options = {}) {
    context.commit(mutationTypes.SET_RESETTING_PASSWORD, true)
    return new LoginApi({
      simulateExample: 'Expired Token'
    })
      .resetPassword({
        body: options.body
      })
      .then(response => {
        if (response.status && ['TokenAttemptFailed', 'ExceededAttempts'].includes(response.status)) {
          setAuthError(context, response.userMessage)
          return false
        } else {
          context.commit(mutationTypes.SET_AUTH_ERROR, false)
          context.commit(mutationTypes.SET_RESETTING_PASSWORD, false)
          context.commit('SET_ANNOUNCEMENT', response.userMessage, { root: true })
          context.dispatch(actionTypes.SET_LOGIN_DETAILS, response)
        }
      })
      .catch(error => {
        if (error.response) {
          error.response.json().then(json => {
            setAuthError(context, json.userMessage)
          })
          return false
        }
      })
      .finally(() => {
        context.commit(mutationTypes.SET_RESETTING_PASSWORD, false)
      })
  },

  // If your password has expired, you'd need to update it!
  [ actionTypes.UPDATE_PASSWORD ] (context, options = {}) {
    context.commit(mutationTypes.SET_UPDATING_PASSWORD, true)
    context.commit(mutationTypes.LOG_USER_OUT)

    return new LoginApi({
      simulateExample: 'Reset'
    })
      .updatePassword({
        body: options.body
      })
      .then(response => {
        context.commit(mutationTypes.SET_UPDATING_PASSWORD, false)

        let errorStatuses = [
          '440',
          'TokenAttemptFailed',
          'PasswordUpdateFailed',
          'ExceededAttempts'
        ]

        if (errorStatuses.includes(response.status)) {
          context.commit(mutationTypes.SET_AUTH_ERROR, true)
          context.commit(mutationTypes.SET_AUTH_MESSAGE, response.message || response.userMessage)
          return false
        } else {
          context.commit(mutationTypes.SET_AUTH_ERROR, false)
          context.dispatch(actionTypes.SET_LOGIN_DETAILS, response)
        }
      })
      .catch(error => {
        if (error.response) {
          error.response.json().then(json => {
            context.commit(mutationTypes.SET_UPDATING_PASSWORD, false)
            context.commit(mutationTypes.SET_AUTH_ERROR, true)
            context.commit(mutationTypes.SET_AUTH_MESSAGE, json.userMessage)
            return false
          })
        }
      })
  },

  [ actionTypes.UPDATE_USER_PASSWORD ] (context, options = {}) {
    context.commit(mutationTypes.SET_UPDATING_PASSWORD, true)
    return new LoginApi({
      // simulateExample: 'Expired Token'
    })
      .updateUserPassword({
        body: options.body
      })
      .then(response => {
        if (response.status && response.status === 'PasswordUpdateFailed') {
          context.commit(mutationTypes.SET_AUTH_ERROR, true)
          context.commit(mutationTypes.SET_AUTH_MESSAGE, response.userMessage)
        } else {
          context.commit(mutationTypes.SET_AUTH_ERROR, false)
          return true
        }
      })
      .catch(error => {
        if (error.response) {
          error.response.json().then(json => {
            context.commit(mutationTypes.SET_AUTH_ERROR, true)
            context.commit(mutationTypes.SET_AUTH_MESSAGE, json.userMessage)
            return false
          })
        }
      })
      .finally(() => {
        context.commit(mutationTypes.SET_UPDATING_PASSWORD, false)
      })
  },

  [ actionTypes.UPDATE_YOUR_PASSWORD ] (context, options = {}) {
    context.commit(mutationTypes.SET_UPDATING_PASSWORD, true)
    return new LoginApi({
      // simulateExample: 'Expired Token'
    })
      .updateYourPassword({
        body: options.body
      })
      .then(response => {
        if (response.status !== 'PasswordUpdated') {
          context.commit(mutationTypes.SET_AUTH_ERROR, true)
          context.commit(mutationTypes.SET_AUTH_MESSAGE, response.message || response.userMessage)
          return false
        } else {
          context.commit(mutationTypes.SET_AUTH_ERROR, false)
          return true
        }
      })
      .catch(error => {
        if (error.response) {
          error.response.json().then(json => {
            context.commit(mutationTypes.SET_UPDATING_PASSWORD, false)
            context.commit(mutationTypes.SET_AUTH_ERROR, true)
            context.commit(mutationTypes.SET_AUTH_MESSAGE, json.userMessage)
            return false
          })
        }
      })
      .finally(() => context.commit(mutationTypes.SET_UPDATING_PASSWORD, false))
  },

  [ actionTypes.LOGIN ] (context, options = {}) {
    context.commit(mutationTypes.RESET_LOGIN_MESSAGES)
    context.commit(mutationTypes.SET_LOGGING_IN, true)
    context.commit(mutationTypes.SET_USER_ID, options.body.userId)
    return new LoginApi(options.options)
      .userAuth({
        body: options.body,
        params: options.params
      })
      .then(response => {
        if (response.status === 'TokenRequested') {
          context.commit(mutationTypes.SET_TOKEN_USER_MESSAGE, response.userMessage)
        }
        // TODO:  reeeeeally need to find out why the OG devs kept making
        //   actions that just wrap a mutation, for what SHOULD be just a mutation.
        // console.log('auth LOGIN pre', response);
        context.dispatch(actionTypes.SET_LOGIN_DETAILS, response)
        context.commit(mutationTypes.SET_RESETTING_INCORRECT_LOGIN_COUNTER)
        return true
      })
      .catch(error => {
        if (error.response) {
          error.response.json().then(
            responseBody => {
              context.commit(mutationTypes.AUTH_FAILED, responseBody)
            }
          )
        }
        return false
      })
      .finally(() => {
        context.commit(mutationTypes.SET_LOGGING_IN, false)
      })
  },

  [ actionTypes.LOGIN_WITH_SSO ] (context, options = {}) {
    context.commit(mutationTypes.SET_LOGGING_IN, true)
    context.commit(mutationTypes.SET_USER_ID, options.body.userId)
    context.commit(mutationTypes.SET_SSO_AUTH_ERROR, false)
    return new LoginApi(options.options)
      .ssoUserAuth({
        body: options.body,
        params: options.params
      })
      .then(response => {
        context.dispatch(actionTypes.SET_LOGIN_DETAILS, response)
        return true
      })
      .catch(error => {
        if (error.response) {
          error.response.json().then(
            responseBody => {
              context.commit(mutationTypes.SET_SSO_AUTH_ERROR, true)
            }
          )
        }
        return false
      })
      .finally(() => {
        context.commit(mutationTypes.SET_LOGGING_IN, false)
      })
  },

  [ actionTypes.REGISTER_USER ] (context, options = {}) {
    context.commit(mutationTypes.SET_RESETTING_PASSWORD, true)
    return new LoginApi({
      simulateExample: 'Register User - Successful'
    })
      .registerUser({
        body: options.body
      })
      .then(response => {
        context.commit(mutationTypes.SET_AUTH_ERROR, false)
        context.commit(mutationTypes.SET_RESETTING_PASSWORD, false)
        context.commit('SET_ANNOUNCEMENT', response.userMessage, {
          root: true
        })
        // console.log('REGISTER_USER before');
        context.dispatch(actionTypes.SET_LOGIN_DETAILS, response)
        // console.log('REGISTER_USER after');
      })
      .catch(error => {
        if (error.response) {
          error.response.json().then(json => {
            context.commit(mutationTypes.SET_AUTH_ERROR, true)
            context.commit(mutationTypes.SET_AUTH_MESSAGE, json.userMessage)
          })
        }

        return false
      })
      .finally(() => {
        context.commit(mutationTypes.SET_RESETTING_PASSWORD, false)
      })
  },

  [ actionTypes.SESSION_HEALTH_CHECK ] ({ dispatch, commit, getters, rootGetters }) {
    return new LoginApi().getSessionHealth().then(response => {
      if (response.status === MAINTENANCE_MODE) { 
        window.location.href = response.redirectURL; 
      }
    }).catch(err => {
      if(err.message.includes('403') || err.message.includes('401')) {
        // clear the polling handle
        commit('CLEAR_ATTESTATION_POLLING_HANDLE', null, { root: true });
      }
    }) 
  },

  [ actionTypes.SET_LOGIN_DETAILS ] (context, loginResponse) {
    context.commit(mutationTypes.SET_LOGIN_DETAILS, loginResponse);
    if(loginResponse.role.userType === 'External') {
      context.dispatch('groupSelector/SET_EXTERNAL_GROUPS', null, { root: true });
    }
  },

  [actionTypes.AUTH_TOKEN_VERIFY](context, options = {}) {
    context.commit(mutationTypes.SET_REQUESTING_NEW_TOKEN, true)
    context.commit(mutationTypes.SET_LOGGING_IN, true)

    return new LoginApi({
      simulateExample: 'Success'
    })
      .authTokenVerify({
        body: options.body
      })
      .then((response) => {
        if (response.status === '440' || response.status === 440 || response.status === 'TokenAttemptFailed' || response.status === 'ExceededAttempts') {
          context.commit(mutationTypes.SET_AUTH_ERROR, true)
          context.commit(mutationTypes.SET_AUTH_MESSAGE, response.userMessage || response.message)
          return false
        } else {
          context.commit(mutationTypes.SET_AUTH_ERROR, false)
          context.commit(mutationTypes.SET_RESETTING_INCORRECT_LOGIN_COUNTER)
          context.commit(mutationTypes.SET_REQUESTING_NEW_TOKEN, false)
          context.dispatch(actionTypes.SET_LOGIN_DETAILS, response)
          return true
        }
      })
      .catch((error) => {
        if (error.response) {
          error.response.json().then((responseBody) => {
            context.commit(mutationTypes.AUTH_FAILED, responseBody)
          })
        }
        return false
      })
      .finally(() => {
        context.commit(mutationTypes.SET_LOGGING_IN, false)
        context.commit(mutationTypes.SET_REQUESTING_NEW_TOKEN, false)
      })
  },
}
